import { useCallback, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { FETCH_CONFLICTS } from 'src/redux/pull-request/actions';
import { getCurrentRepositoryFullSlug } from 'src/selectors/repository-selectors';
import authRequest from 'src/utils/fetch';

import { useDiffSetUrls } from '../urls';

export const useFetchConflictsActions = (): {
  fetchConflicts: (conflictsUrl: string) => Promise<void>;
  isLoading: boolean;
} => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);

  const fetchConflicts = useCallback(
    async (conflictsUrl: string) => {
      try {
        setIsLoading(true);
        const conflictsResponse = await fetch(authRequest(conflictsUrl));
        if (conflictsResponse.ok) {
          const conflicts = await conflictsResponse.json();
          dispatch({
            type: FETCH_CONFLICTS.SUCCESS,
            payload: conflicts,
          });
        }
      } catch (e) {
        // failing silently since we don't do anything on error
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch]
  );

  return {
    fetchConflicts,
    isLoading,
  };
};

export function useFetchConflicts(
  sourceHash: string,
  destHash: string,
  destRepoName: string
): { isLoading: boolean } {
  const repositoryFullSlug = useSelector(getCurrentRepositoryFullSlug) || '';

  const { conflictsUrl } = useDiffSetUrls(
    repositoryFullSlug,
    sourceHash,
    destRepoName,
    destHash
  );

  const { fetchConflicts, isLoading } = useFetchConflictsActions();

  useEffect(() => {
    if (!sourceHash || !destHash) {
      return;
    }
    fetchConflicts(conflictsUrl());
    // The url is missing because during an update to the dest repo and dest hash
    // the dest repo can update before the dest hash, which results in a call with
    // old dest hash and new dest hash
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchConflicts, sourceHash, destHash]);

  return { isLoading };
}
